// src/hooks/useAnalytics.ts
import { useCallback } from 'react';
import { useUser } from '../context/UserInfoProvider';
import {
  track,
  trackFeatureUsage,
  trackStaffAction,
  trackGuestService,
  trackBusinessMetric,
  trackUserError,
  isAnalyticsEnabled,
} from '../utils/analytics';

/**
 * Hook to provide analytics tracking with user context pre-populated
 * Following the same patterns as other hooks in the codebase
 */
export const useAnalytics = () => {
  const { hotelId, employeeId, userInfo } = useUser();

  // Get common context properties
  const getCommonProps = useCallback(() => ({
    hotelId,
    employeeId,
    role: userInfo?.roleId,
  }), [hotelId, employeeId, userInfo?.roleId]);

  // Track generic events with context
  const trackEvent = useCallback((eventName: string, properties?: Record<string, any>) => {
    const commonProps = getCommonProps();
    track(eventName, {
      ...commonProps,
      ...properties,
    });
  }, [getCommonProps]);

  // Track feature usage with context
  const trackFeature = useCallback((feature: string, properties?: Record<string, any>) => {
    const commonProps = getCommonProps();
    trackFeatureUsage(feature, {
      ...commonProps,
      ...properties,
    });
  }, [getCommonProps]);

  // Track staff actions with context
  const trackStaff = useCallback((action: string, properties?: Record<string, any>) => {
    const commonProps = getCommonProps();
    trackStaffAction(action, {
      ...commonProps,
      ...properties,
    });
  }, [getCommonProps]);

  // Track guest service interactions with context
  const trackGuest = useCallback((service: string, outcome: string, properties?: Record<string, any>) => {
    const commonProps = getCommonProps();
    trackGuestService(service, outcome, {
      ...commonProps,
      ...properties,
    });
  }, [getCommonProps]);

  // Track business metrics with context
  const trackMetric = useCallback((metric: string, value: number, properties?: Record<string, any>) => {
    const commonProps = getCommonProps();
    trackBusinessMetric(metric, value, {
      ...commonProps,
      ...properties,
    });
  }, [getCommonProps]);

  // Track user errors with context
  const trackError = useCallback((component: string, userAction: string, severity: string, properties?: Record<string, any>) => {
    const commonProps = getCommonProps();
    trackUserError(component, userAction, severity, {
      ...commonProps,
      ...properties,
    });
  }, [getCommonProps]);

  // Common event patterns used throughout the app
  const analytics = {
    // Generic tracking
    track: trackEvent,
    
    // Feature tracking
    feature: trackFeature,
    
    // Staff action tracking
    staff: trackStaff,
    
    // Guest service tracking
    guest: trackGuest,
    
    // Business metrics tracking
    metric: trackMetric,
    
    // Error tracking
    error: trackError,
    
    // Convenience methods for common actions
    search: useCallback((query: string, resultCount?: number, component?: string) => {
      trackFeature('search', {
        query_length: query.length,
        result_count: resultCount,
        component,
      });
    }, [trackFeature]),

    upload: useCallback((entityType: string, fileType: string, fileSize: number, success: boolean) => {
      trackFeature('file_upload', {
        entityType,
        fileType,
        fileSize,
        success,
      });
    }, [trackFeature]),

    formSubmit: useCallback((formName: string, fieldCount: number, success: boolean, duration?: number) => {
      trackFeature('form_submit', {
        form_name: formName,
        field_count: fieldCount,
        success,
        duration_ms: duration,
      });
    }, [trackFeature]),

    pageInteraction: useCallback((element: string, action: string, properties?: Record<string, any>) => {
      trackEvent('page_interaction', {
        element,
        action,
        ...properties,
      });
    }, [trackEvent]),

    // Business-specific convenience methods
    announcement: {
      create: useCallback((priority: string, audience: string, messageLength: number) => {
        trackStaff('announcement_created', {
          priority,
          audience,
          message_length: messageLength,
        });
      }, [trackStaff]),

      send: useCallback((recipientCount: number, priority: string) => {
        trackStaff('announcement_sent', {
          recipient_count: recipientCount,
          priority,
        });
      }, [trackStaff]),
    },

    request: {
      process: useCallback((requestType: string, outcome: string, processingTime?: number) => {
        trackGuest('request_processed', outcome, {
          request_type: requestType,
          processing_time_ms: processingTime,
        });
      }, [trackGuest]),

      statusChange: useCallback((requestType: string, newStatus: string, fromStatus?: string) => {
        trackStaff('request_status_updated', {
          request_type: requestType,
          new_status: newStatus,
          from_status: fromStatus,
        });
      }, [trackStaff]),
    },

    profile: {
      save: useCallback((profileType: string, fieldCount: number, success: boolean) => {
        trackFeature('profile_save', {
          profile_type: profileType,
          field_count: fieldCount,
          success,
        });
      }, [trackFeature]),

      view: useCallback((profileType: string, viewedUserId?: string) => {
        trackFeature('profile_view', {
          profile_type: profileType,
          viewed_user_id: viewedUserId,
        });
      }, [trackFeature]),
    },
  };

  return {
    ...analytics,
    isEnabled: isAnalyticsEnabled(),
    context: getCommonProps(),
  };
};
